import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, RefreshCw, Search, ExternalLink, Calendar, Key, Server, Upload, Eye, EyeOff } from 'lucide-react';
import { useHostings, Hosting, NewHosting } from '@/hooks/useHostings';

interface HostingsTableProps {
  className?: string;
}

const HostingsTable: React.FC<HostingsTableProps> = ({ className }) => {
  const { hostings, loading, createHosting, createHostingsBulk, updateHosting, deleteHosting, renewHostingExpiry } = useHostings();

  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('all');

  // State for password visibility
  const [visiblePasswords, setVisiblePasswords] = useState<Set<string>>(new Set());

  // State for forms
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isBulkAddDialogOpen, setIsBulkAddDialogOpen] = useState(false);
  const [editingHosting, setEditingHosting] = useState<Hosting | null>(null);
  const [formData, setFormData] = useState<NewHosting>({
    name: '',
    panel_url: '',
    panel_login: '',
    panel_password: '',
    ftp_login: '',
    ftp_password: '',
    dns1: '',
    dns2: '',
    ip_address: '',
    expiry_date: ''
  });
  const [bulkText, setBulkText] = useState('');

  // Password visibility toggle
  const togglePasswordVisibility = (hostingId: string) => {
    setVisiblePasswords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(hostingId)) {
        newSet.delete(hostingId);
      } else {
        newSet.add(hostingId);
      }
      return newSet;
    });
  };

  // Filtered hostings
  const filteredHostings = hostings.filter(hosting => {
    const matchesSearch = hosting.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         hosting.panel_url.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesExpiry = expiryFilter === 'all' ||
                         (expiryFilter === 'expiring-soon' && hosting.expiry_date && getDaysUntilExpiry(hosting.expiry_date) > 0 && getDaysUntilExpiry(hosting.expiry_date) <= 30) ||
                         (expiryFilter === 'expired' && hosting.expiry_date && getDaysUntilExpiry(hosting.expiry_date) < 0);

    return matchesSearch && matchesExpiry;
  });

  // Helper functions
  const getDaysUntilExpiry = (expiryDate: string) => {
    if (!expiryDate) return Infinity;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for consistent comparison

    const expiry = new Date(expiryDate);
    if (isNaN(expiry.getTime())) return Infinity; // Invalid date

    expiry.setHours(0, 0, 0, 0); // Reset time to start of day

    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getExpiryBadgeVariant = (expiryDate?: string) => {
    if (!expiryDate) return 'secondary';

    const days = getDaysUntilExpiry(expiryDate);
    if (days < 0) return 'destructive';
    if (days <= 7) return 'destructive';
    if (days <= 30) return 'outline';
    return 'default';
  };

  const getExpiryText = (expiryDate?: string) => {
    if (!expiryDate) return 'Brak daty';

    const days = getDaysUntilExpiry(expiryDate);
    if (days < 0) return `Wygasło ${Math.abs(days)} dni temu`;
    if (days === 0) return 'Wygasa dzisiaj';
    if (days === 1) return 'Wygasa jutro';
    return `Zostało ${days} dni`;
  };

  // Form handlers
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingHosting) {
      await updateHosting(editingHosting.id, formData);
    } else {
      await createHosting(formData);
    }

    resetForm();
    setIsAddDialogOpen(false);
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const lines = bulkText.split('\n').filter(line => line.trim());
    const hostingsToAdd: NewHosting[] = [];

    for (const line of lines) {
      const parts = line.split(';').map(part => part.trim());
      if (parts.length >= 2) {
        hostingsToAdd.push({
          name: parts[0] || 'EMPTY',
          panel_url: parts[1] || 'EMPTY',
          panel_login: parts[2] || 'EMPTY',
          panel_password: parts[3] || 'EMPTY',
          ftp_login: parts[4] || 'EMPTY',
          ftp_password: parts[5] || 'EMPTY',
          dns1: parts[6] || 'EMPTY',
          dns2: parts[7] || 'EMPTY',
          ip_address: parts[8] || 'EMPTY',
          expiry_date: parts[9] || 'EMPTY',
        });
      }
    }

    if (hostingsToAdd.length > 0) {
      await createHostingsBulk(hostingsToAdd);
      setBulkText('');
      setIsBulkAddDialogOpen(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      panel_url: '',
      panel_login: 'EMPTY',
      panel_password: 'EMPTY',
      ftp_login: 'EMPTY',
      ftp_password: 'EMPTY',
      expiry_date: 'EMPTY'
    });
    setEditingHosting(null);
  };

  const startEdit = (hosting: Hosting) => {
    setEditingHosting(hosting);
    setFormData({
      name: hosting.name,
      panel_url: hosting.panel_url,
      panel_login: hosting.panel_login || 'EMPTY',
      panel_password: hosting.panel_password || 'EMPTY',
      ftp_login: hosting.ftp_login || 'EMPTY',
      ftp_password: hosting.ftp_password || 'EMPTY',
      dns1: hosting.dns1 || 'EMPTY',
      dns2: hosting.dns2 || 'EMPTY',
      ip_address: hosting.ip_address || 'EMPTY',
      expiry_date: hosting.expiry_date || 'EMPTY'
    });
    setIsAddDialogOpen(true);
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Hostingi</CardTitle>
          <CardDescription>Ładowanie danych...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              Hostingi
            </CardTitle>
            <CardDescription>
              Zarządzaj swoimi kontami hostingowymi
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Dialog open={isBulkAddDialogOpen} onOpenChange={setIsBulkAddDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Upload className="h-4 w-4 mr-2" />
                  Dodaj bulk
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Dodaj hostingi hurtowo</DialogTitle>
                  <DialogDescription>
                    Wklej dane hostingów w formacie: nazwa;url_panelu;login_panelu;hasło_panelu;login_ftp;hasło_ftp;dns1;dns2;ip_adres;data_wygaśnięcia
                    <br />
                    Każda hosting w osobnej linii. Puste pola będą oznaczone jako "EMPTY"
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleBulkSubmit}>
                  <div className="space-y-4">
                    <Textarea
                      placeholder="HomePL;https://panel.home.pl;admin123;password123;ftp_user;ftp_pass;ns1.home.pl;ns2.home.pl;123.456.789.012;2025-12-31&#10;Hostinger;https://cpanel.hostinger.com;user456;pass456;ftpuser;ftppass;ns1.hostinger.com;ns2.hostinger.com;987.654.321.098;2026-01-15"
                      value={bulkText}
                      onChange={(e) => setBulkText(e.target.value)}
                      rows={8}
                    />
                  </div>
                  <DialogFooter className="mt-4">
                    <Button type="button" variant="outline" onClick={() => setIsBulkAddDialogOpen(false)}>
                      Anuluj
                    </Button>
                    <Button type="submit">Dodaj hostingi</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
              setIsAddDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Dodaj hosting
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingHosting ? 'Edytuj hosting' : 'Dodaj nowy hosting'}
                </DialogTitle>
                <DialogDescription>
                  Wypełnij dane konta hostingowego
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleFormSubmit}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nazwa hostingu *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="panel_url">URL panelu *</Label>
                    <Input
                      id="panel_url"
                      type="url"
                      value={formData.panel_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, panel_url: e.target.value }))}
                      placeholder="https://panel.hosting.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="panel_login">Login do panelu</Label>
                    <Input
                      id="panel_login"
                      value={formData.panel_login}
                      onChange={(e) => setFormData(prev => ({ ...prev, panel_login: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="panel_password">Hasło do panelu</Label>
                    <Input
                      id="panel_password"
                      type="password"
                      value={formData.panel_password}
                      onChange={(e) => setFormData(prev => ({ ...prev, panel_password: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ftp_login">Login FTP</Label>
                    <Input
                      id="ftp_login"
                      value={formData.ftp_login}
                      onChange={(e) => setFormData(prev => ({ ...prev, ftp_login: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ftp_password">Hasło FTP</Label>
                    <Input
                      id="ftp_password"
                      type="password"
                      value={formData.ftp_password}
                      onChange={(e) => setFormData(prev => ({ ...prev, ftp_password: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dns1">DNS1</Label>
                    <Input
                      id="dns1"
                      type="text"
                      placeholder="ns1.domena.com"
                      value={formData.dns1}
                      onChange={(e) => setFormData(prev => ({ ...prev, dns1: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dns2">DNS2</Label>
                    <Input
                      id="dns2"
                      type="text"
                      placeholder="ns2.domena.com"
                      value={formData.dns2}
                      onChange={(e) => setFormData(prev => ({ ...prev, dns2: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ip_address">IP Adres</Label>
                    <Input
                      id="ip_address"
                      type="text"
                      placeholder="123.456.789.012"
                      value={formData.ip_address}
                      onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="expiry_date">Data wygaśnięcia</Label>
                    <Input
                      id="expiry_date"
                      type="date"
                      value={formData.expiry_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                    />
                  </div>
                </div>
                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    Anuluj
                  </Button>
                  <Button type="submit">
                    {editingHosting ? 'Zaktualizuj' : 'Dodaj hosting'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Szukaj hostingów..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <select
            value={expiryFilter}
            onChange={(e) => setExpiryFilter(e.target.value)}
            className="px-3 py-2 border border-input rounded-md bg-background text-sm"
          >
            <option value="all">Wszystkie</option>
            <option value="expiring-soon">Wygasają wkrótce (&lt;30 dni)</option>
            <option value="expired">Wygasłe</option>
          </select>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Nazwa</TableHead>
                <TableHead>Panel</TableHead>
                <TableHead>Login panel</TableHead>
                <TableHead>Hasło panel</TableHead>
                <TableHead>Login FTP</TableHead>
                <TableHead>Hasło FTP</TableHead>
                <TableHead>DNS1</TableHead>
                <TableHead>DNS2</TableHead>
                <TableHead>IP Adres</TableHead>
                <TableHead>Wygaśnięcie</TableHead>
                <TableHead className="w-32">Akcje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredHostings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                    {hostings.length === 0 ? 'Brak hostingów. Dodaj pierwszy hosting.' : 'Brak hostingów spełniających kryteria filtrowania.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredHostings.map((hosting) => (
                  <TableRow key={hosting.id}>
                    <TableCell className="font-medium">{hosting.name}</TableCell>
                    <TableCell>
                      <a
                        href={hosting.panel_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {hosting.panel_url}
                      </a>
                    </TableCell>
                    <TableCell>
                      {hosting.panel_login ? (
                        <span className="flex items-center gap-1">
                          <Key className="h-3 w-3" />
                          {hosting.panel_login}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {hosting.panel_password ? (
                        <div className="flex items-center gap-2">
                          {visiblePasswords.has(hosting.id) ? (
                            <>
                              <span className="text-sm font-mono">{hosting.panel_password}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => togglePasswordVisibility(hosting.id)}
                                className="h-6 w-6 p-0 hover:bg-transparent"
                              >
                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <span className="text-sm">••••••••</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => togglePasswordVisibility(hosting.id)}
                                className="h-6 w-6 p-0 hover:bg-transparent"
                              >
                                <Eye className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {hosting.ftp_login ? (
                        <span className="flex items-center gap-1">
                          <Key className="h-3 w-3" />
                          {hosting.ftp_login}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {hosting.ftp_password ? (
                        <div className="flex items-center gap-2">
                          {visiblePasswords.has(`${hosting.id}-ftp`) ? (
                            <>
                              <span className="text-sm font-mono">{hosting.ftp_password}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => togglePasswordVisibility(`${hosting.id}-ftp`)}
                                className="h-6 w-6 p-0 hover:bg-transparent"
                              >
                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <span className="text-sm">••••••••</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => togglePasswordVisibility(`${hosting.id}-ftp`)}
                                className="h-6 w-6 p-0 hover:bg-transparent"
                              >
                                <Eye className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {hosting.dns1 ? (
                        <span className="font-mono text-sm">{hosting.dns1}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {hosting.dns2 ? (
                        <span className="font-mono text-sm">{hosting.dns2}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {hosting.ip_address ? (
                        <span className="font-mono text-sm">{hosting.ip_address}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {hosting.expiry_date ? (
                        <Badge variant={getExpiryBadgeVariant(hosting.expiry_date)} className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {getExpiryText(hosting.expiry_date)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEdit(hosting)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>

                        {hosting.expiry_date && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => renewHostingExpiry(hosting.id)}
                            className="h-8 w-8 p-0"
                            title="Hosting odnowiony"
                          >
                            <RefreshCw className="h-3 w-3" />
                          </Button>
                        )}

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Usuń hosting</AlertDialogTitle>
                              <AlertDialogDescription>
                                Czy na pewno chcesz usunąć hosting <strong>{hosting.name}</strong>?
                                <br />
                                Tej operacji nie można cofnąć.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Anuluj</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteHosting(hosting.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Usuń
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default HostingsTable;
